import { reflect } from '@effector/reflect';
import { useUnit } from 'effector-react';
import { Trash2 } from 'lucide-react';
import { not, equals } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { authModel, USER_ROLES } from 'shared/api';
import { translated } from 'shared/lib';
import { ConfirmModalProps, createConfirmModal } from 'shared/ui';
import { DeleteDeviceDetailedModel } from '../../model';

export const createDeleteDeviceDetailedModal = (model: DeleteDeviceDetailedModel) => {
	const ConfirmModal = createConfirmModal(model);

	const DeleteDeviceDetailedModal = forwardRef<HTMLDivElement, ConfirmModalProps>(
		({ ...props }, ref) => {
			const { t } = useTranslation();

			const [device] = useUnit([model.$modalData]);

			return (
				<ConfirmModal
					ref={ref}
					title={t('delete-device.title')}
					cancelButtonProps={{
						children: t('delete-device.cancel'),
					}}
					submitButtonProps={{
						children: t('delete-device.delete'),
						variant: 'destructive',
					}}
					{...props}
				>
					{t('delete-device.description', { name: device?.name ?? '' })}
				</ConfirmModal>
			);
		},
	);

	const DeleteDeviceDetailedModalButton = reflect({
		view: ConfirmModal.Button,
		bind: {
			'data-test-id': 'delete-device-detailed-button',
			variant: 'destructive',
			leftSection: <Trash2 />,
			children: translated('delete-device.delete'),
			disabled: not(equals(authModel.$role, USER_ROLES.god)),
		},
	});

	const DeleteDeviceDetailedModalActionIcon = reflect({
		view: ConfirmModal.ActionIcon,
		bind: {
			'data-test-id': 'delete-device-detailed-action-icon',
			variant: 'ghost-destructive',
			children: <Trash2 />,
			disabled: not(equals(authModel.$role, USER_ROLES.god)),
		},
	});

	return Object.assign(DeleteDeviceDetailedModal, {
		Button: DeleteDeviceDetailedModalButton,
		ActionIcon: DeleteDeviceDetailedModalActionIcon,
	});
};
